import React, { useState, useEffect } from 'react';
import { useTrainingSocket } from '../../context/TrainingSocketContext';
import './ChangeHistoryViewer.css';

interface Iteration {
  iteration: number;
  status: string;
  timestamp: string;
  epoch_triggered: number;
  reason: string;
  trigger_metrics?: {
    current_train_loss?: number;
    current_val_loss?: number;
    learning_rate?: number;
    avg_gradient_norm?: number;
  };
  changes?: {
    'architecture.json'?: {
      summary?: string;
      diff?: any;
    };
    'config.json'?: {
      summary?: string;
      diff?: any;
    };
  };
  llm_suggestions?: {
    analysis?: string;
    new_recommendations?: string[];
    proposed_architecture_changes?: any;
    proposed_config_changes?: any;
  };
}

interface IterationHistory {
  training_session_id: string;
  started_at: string;
  current_iteration: number;
  regeneration_count: number;
  max_regeneration_count: number;
  iterations: Iteration[];
}

const ChangeHistoryViewer: React.FC = () => {
  const { socket } = useTrainingSocket();
  const [history, setHistory] = useState<IterationHistory | null>(null);
  const [selectedIteration, setSelectedIteration] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIterations, setExpandedIterations] = useState<Set<number>>(new Set([0]));

  useEffect(() => {
    if (!socket) return;

    let timeoutId: NodeJS.Timeout;
    let responseReceived = false;

    // Request history on component mount
    socket.emit('getReevaluationHistory');

    // Listen for history response
    const handleHistoryResponse = (data: any) => {
      responseReceived = true;
      clearTimeout(timeoutId);

      if (data.success && data.history) {
        setHistory(data.history);
        setSelectedIteration(data.history.current_iteration || 0);
        setError(null);
      } else {
        // If no history, set empty state instead of error
        setHistory(null);
        setError(null);
      }
      setLoading(false);
    };

    socket.on('reevaluationHistory', handleHistoryResponse);

    // Set a 3-second timeout - if no response, show empty state
    timeoutId = setTimeout(() => {
      if (!responseReceived) {
        setHistory(null);
        setError(null);
        setLoading(false);
        console.warn('Change history request timed out');
      }
    }, 3000);

    return () => {
      clearTimeout(timeoutId);
      socket.off('reevaluationHistory', handleHistoryResponse);
    };
  }, [socket]);

  const toggleIterationExpand = (iterationNum: number) => {
    const newExpanded = new Set(expandedIterations);
    if (newExpanded.has(iterationNum)) {
      newExpanded.delete(iterationNum);
    } else {
      newExpanded.add(iterationNum);
    }
    setExpandedIterations(newExpanded);
  };

  if (loading) {
    return (
      <div className="change-history-loader">
        <p>Loading change history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="change-history-error">
        <p>Error: {error}</p>
      </div>
    );
  }

  if (!history || history.iterations.length === 0) {
    return (
      <div className="change-history-empty">
        <h3>📋 Change History</h3>
        <p>No model iterations yet.</p>
        <p style={{ fontSize: '0.875rem', color: '#94a3b8', marginTop: '1rem' }}>
          Model changes and iterations will appear here when:
        </p>
        <ul style={{ fontSize: '0.875rem', color: '#94a3b8', textAlign: 'left', display: 'inline-block' }}>
          <li>Training is in progress</li>
          <li>Model performance plateaus</li>
          <li>Training loss diverges</li>
          <li>Gradients become vanishing/exploding</li>
          <li>Learning rate becomes too small</li>
        </ul>
        <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '1rem' }}>
          Currently viewing an empty history. Training will populate this view as needed.
        </p>
      </div>
    );
  }

  return (
    <div className="change-history-viewer">
      <div className="change-history-summary">
        <div className="summary-stat">
          <span className="stat-label">Current Iteration:</span>
          <span className="stat-value">{history.current_iteration}</span>
        </div>
        <div className="summary-stat">
          <span className="stat-label">Regenerations:</span>
          <span className="stat-value">{history.regeneration_count}/{history.max_regeneration_count}</span>
        </div>
        <div className="summary-stat">
          <span className="stat-label">Session:</span>
          <span className="stat-value">{history.training_session_id}</span>
        </div>
      </div>

      <div className="iterations-list">
        {history.iterations.map((iteration) => (
          <div
            key={iteration.iteration}
            className={`iteration-item ${iteration.status} ${expandedIterations.has(iteration.iteration) ? 'expanded' : ''}`}
          >
            <div
              className="iteration-header"
              onClick={() => toggleIterationExpand(iteration.iteration)}
            >
              <div className="iteration-title">
                <span className="iteration-number">Iteration {iteration.iteration}</span>
                <span className="iteration-status">{iteration.status}</span>
                <span className="iteration-reason">{iteration.reason}</span>
              </div>
              <div className="iteration-timestamp">
                {new Date(iteration.timestamp).toLocaleString()}
              </div>
              <span className="expand-icon">{expandedIterations.has(iteration.iteration) ? '▼' : '▶'}</span>
            </div>

            {expandedIterations.has(iteration.iteration) && (
              <div className="iteration-details">
                {iteration.epoch_triggered !== undefined && (
                  <div className="detail-section">
                    <h4>Trigger Information</h4>
                    <p><strong>Epoch Triggered:</strong> {iteration.epoch_triggered}</p>
                    {iteration.trigger_metrics && (
                      <>
                        <p><strong>Train Loss:</strong> {iteration.trigger_metrics.current_train_loss?.toFixed(4)}</p>
                        <p><strong>Val Loss:</strong> {iteration.trigger_metrics.current_val_loss?.toFixed(4)}</p>
                        <p><strong>Learning Rate:</strong> {iteration.trigger_metrics.learning_rate?.toExponential(2)}</p>
                        <p><strong>Gradient Norm:</strong> {iteration.trigger_metrics.avg_gradient_norm?.toFixed(6)}</p>
                      </>
                    )}
                  </div>
                )}

                {iteration.changes && (
                  <>
                    {iteration.changes['architecture.json'] && (
                      <div className="detail-section">
                        <h4>Architecture Changes</h4>
                        <p className="change-summary">{iteration.changes['architecture.json'].summary}</p>
                        {iteration.changes['architecture.json'].diff && (
                          <details className="change-diff">
                            <summary>View Details</summary>
                            <pre>{JSON.stringify(iteration.changes['architecture.json'].diff, null, 2)}</pre>
                          </details>
                        )}
                      </div>
                    )}

                    {iteration.changes['config.json'] && (
                      <div className="detail-section">
                        <h4>Config Changes</h4>
                        <p className="change-summary">{iteration.changes['config.json'].summary}</p>
                        {iteration.changes['config.json'].diff && (
                          <details className="change-diff">
                            <summary>View Details</summary>
                            <pre>{JSON.stringify(iteration.changes['config.json'].diff, null, 2)}</pre>
                          </details>
                        )}
                      </div>
                    )}
                  </>
                )}

                {iteration.llm_suggestions && (
                  <div className="detail-section llm-suggestions">
                    <h4>LLM Analysis</h4>
                    {iteration.llm_suggestions.analysis && (
                      <p><strong>Analysis:</strong> {iteration.llm_suggestions.analysis}</p>
                    )}
                    {iteration.llm_suggestions.new_recommendations && (
                      <>
                        <strong>Recommendations:</strong>
                        <ul>
                          {iteration.llm_suggestions.new_recommendations.map((rec, idx) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChangeHistoryViewer;
